/**
 * Mesure le tapis de jeu et en dérive la géométrie partagée.
 *
 * `PlayerHand` et `TrickDisplay` appellent tous les deux ce hook. Comme la
 * mesure et la formule sont identiques, ils obtiennent nécessairement la même
 * taille de carte.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  computeTableLayout,
  type HandLayoutMode,
  type TableLayout,
} from '../utils/tableLayout';

const FALLBACK_MAX_CARD_HEIGHT = 218;

/** Durée pendant laquelle les transitions sont coupées après un resize. */
const RESIZE_SETTLE_MS = 150;

interface TableMetrics {
  width: number;
  height: number;
  maxCardHeight: number;
  orientation: 'portrait' | 'landscape';
}

const INITIAL_METRICS: TableMetrics = {
  width: 800,
  height: 600,
  maxCardHeight: FALLBACK_MAX_CARD_HEIGHT,
  orientation: 'landscape',
};

function readOrientation(): 'portrait' | 'landscape' {
  if (typeof document === 'undefined') return 'landscape';
  const attr = document.documentElement.getAttribute('data-orientation');
  if (attr === 'portrait' || attr === 'landscape') return attr;
  return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
}

function readMaxCardHeight(): number {
  if (typeof window === 'undefined') return FALLBACK_MAX_CARD_HEIGHT;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--card-large-height');
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : FALLBACK_MAX_CARD_HEIGHT;
}

function findTable(): Element | null {
  return document.querySelector('.game-table') ?? document.querySelector('.training-board');
}

export interface UseTableLayoutOptions {
  handCount: number;
  handLayout: HandLayoutMode;
}

export interface UseTableLayoutResult {
  layout: TableLayout;
  /** Vrai juste après un redimensionnement : les transitions doivent être coupées. */
  isResizing: boolean;
}

export function useTableLayout({
  handCount,
  handLayout,
}: UseTableLayoutOptions): UseTableLayoutResult {
  const [metrics, setMetrics] = useState<TableMetrics>(INITIAL_METRICS);
  const [isResizing, setIsResizing] = useState(false);
  const settleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const measure = useCallback(() => {
    const target = findTable();
    const rect = target?.getBoundingClientRect();
    const width = rect && rect.width > 100 ? rect.width : window.innerWidth;
    const height = rect && rect.height > 100 ? rect.height : window.innerHeight;
    const maxCardHeight = readMaxCardHeight();
    const orientation = readOrientation();

    setMetrics(prev =>
      Math.abs(prev.width - width) < 2 &&
      Math.abs(prev.height - height) < 2 &&
      prev.maxCardHeight === maxCardHeight &&
      prev.orientation === orientation
        ? prev
        : { width, height, maxCardHeight, orientation },
    );
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    // Mesures différées : le tapis se stabilise après les transitions CSS
    // d'ouverture de sidebar / drawer.
    const timers = [50, 150, 300].map(delay => setTimeout(measure, delay));

    const handleResize = () => {
      setIsResizing(true);
      measure();
      if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
      settleTimeoutRef.current = setTimeout(() => setIsResizing(false), RESIZE_SETTLE_MS);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    const observer = new ResizeObserver(handleResize);
    const table = findTable();
    if (table) observer.observe(table);
    const gameLayout = document.querySelector('.game-layout');
    if (gameLayout) observer.observe(gameLayout);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      observer.disconnect();
      timers.forEach(clearTimeout);
      if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
    };
  }, [measure]);

  const layout = useMemo(
    () =>
      computeTableLayout({
        containerWidth: metrics.width,
        containerHeight: metrics.height,
        maxCardHeight: metrics.maxCardHeight,
        handCount,
        handLayout,
        orientation: metrics.orientation,
      }),
    [metrics, handCount, handLayout],
  );

  useLayoutEffect(() => {
    const root = document.querySelector('.game-layout') as HTMLElement | null;
    if (!root) return;
    if (layout.anchorPlayWell) {
      root.style.setProperty('--play-well-height', `${Math.round(layout.playWellHeight)}px`);
    } else {
      root.style.removeProperty('--play-well-height');
    }
  }, [layout.anchorPlayWell, layout.playWellHeight]);

  return { layout, isResizing };
}
